// 戦闘ロジックが未接続のあいだ、盤面の見た目だけを確かめるための静的な盤面（ルールは動かない）。
import { CHARS, ENEMIES, type CharId, type StageInfo } from '@hyakki/core';
import type { VBoard, VTile, VUnit } from './adapter';
import { CELLS, ROWS, cellAt, rowWidth } from './hex';

// mch-dice の meadow 配置（上半分。下半分は180度回転）
const TOP = ['#....#', '.#.C.#.', 'a..H.$', '..#..m.', '.+..#.', 'e.#B#.e'];

export function demoBoard(stage: StageInfo, charId: CharId): VBoard {
  const grid: { ch: string; side: 'blue' | 'red' }[][] = [];
  for (let row = 0; row < ROWS; row++) {
    if (row <= 5) grid.push(TOP[row].split('').map((ch) => ({ ch, side: 'red' as const })));
    else {
      const src = TOP[ROWS - 1 - row];
      const w = rowWidth(row);
      grid.push(Array.from({ length: w }, (_, col) => ({ ch: src[w - 1 - col], side: 'blue' as const })));
    }
  }
  const tiles: VTile[] = [];
  const cores = { blue: { cell: 0, hp: 200, max: 200 }, red: { cell: 0, hp: 160, max: 200 } };
  const homes: VBoard['homes'] = {};
  const camps: number[] = [];
  let boss: number | null = null;
  for (const c of CELLS) {
    const { ch, side } = grid[c.row][c.col];
    const t: VTile = { kind: 'grass', owner: null, relay: false, side: null, pickup: null, v: 0 };
    if (ch === '#') t.kind = 'block';
    else if (ch === 'C') {
      t.kind = 'core';
      t.side = side;
      cores[side].cell = c.i;
    } else if (ch === 'H') {
      t.kind = 'home';
      t.side = side;
      homes[side] = c.i;
    } else if ('ame'.includes(ch)) {
      t.kind = 'camp';
      camps.push(c.i);
    } else if (ch === 'B') {
      if (side === 'red') boss = c.i;
      t.kind = 'camp';
    }
    tiles.push(t);
  }
  for (const [col, row] of [
    [0, 4],
    [5, 6],
    [3, 5],
  ] as const) {
    const i = cellAt(col, row);
    if (i != null && tiles[i].kind === 'grass') tiles[i].relay = true;
  }
  // 自陣の色塗り（見た目の確認用）
  for (const c of CELLS) if (c.row >= 7 && tiles[c.i].kind === 'grass' && c.i % 3) tiles[c.i].owner = 'blue';
  for (const c of CELLS) if (c.row <= 2 && tiles[c.i].kind === 'grass' && c.i % 2) tiles[c.i].owner = 'red';
  const relay = tiles.findIndex((t) => t.relay);
  if (relay >= 0) tiles[relay].owner = 'blue';

  let id = 1;
  const unit = (p: Partial<VUnit> & Pick<VUnit, 'kind' | 'side' | 'cell' | 'name'>): VUnit => ({
    id: id++,
    hp: 60,
    max: 80,
    alive: true,
    role: null,
    stats: {},
    ...p,
  });
  const units: VUnit[] = [];
  const heroCell = cellAt(2, 7) ?? homes.blue ?? 0;
  units.push(unit({ kind: 'hero', side: 'blue', cell: heroCell, name: CHARS[charId].name, charId, lv: 5, hp: 110, max: 140, stats: { range: 2 } }));
  const rival = ENEMIES[`r${String(stage.chapter).padStart(2, '0')}`];
  units.push(unit({ kind: 'rival', side: 'red', cell: homes.red ?? null, name: rival?.name ?? '敵将', enemyId: rival?.id, role: 'rival', lv: 6 }));
  stage.enemyIds.forEach((eid, k) => {
    const cell = camps[k % Math.max(1, camps.length)];
    if (cell == null || units.some((u) => u.cell === cell)) return;
    const e = ENEMIES[eid];
    units.push(unit({ kind: 'neutral', side: 'neutral', cell, name: e?.name ?? '妖怪', enemyId: eid, role: e?.role ?? 'small', engaged: k === 0 }));
  });
  if (boss != null) {
    const bid = stage.bossId ?? ENEMY_BOSS(stage.chapter);
    const e = ENEMIES[bid];
    units.push(unit({ kind: 'boss', side: 'red', cell: boss, name: e?.name ?? 'ボス', enemyId: bid, role: 'boss', hp: 220, max: 260 }));
  }
  units.push(unit({ kind: 'building', side: 'blue', cell: cellAt(4, 8) ?? 0, name: '櫓', btype: 'yagura', hp: 40, max: 40 }));
  units.push(unit({ kind: 'building', side: 'blue', cell: cellAt(1, 8) ?? 0, name: '竹柵', btype: 'sakaku', hp: 30, max: 30 }));
  return {
    ougi: null,
    cast: null,
    zones: [],
    respawns: [],
    tiles,
    units,
    cores,
    homes,
    current: units[0].id,
    terrain: stage.terrain,
    phase: 'start',
    roll: null,
    budget: 0,
    materials: 20,
    potions: 2,
    items: [],
    tier: { blue: 1, red: 0 },
    ruleName: '追い風',
    ruleDesc: '',
    terrTiers: [],
    siege: { blue: 0, red: 0 },
    guardian: false,
    defense: null,
    turnLimit: null,
    coreGuard: 1,
    heroKOs: 0,
    surge: { blue: { ...DEMO_SURGE }, red: { ...DEMO_SURGE } },
    dashing: false,
    territory: { blue: tiles.filter((t) => t.owner === 'blue').length / 60, red: tiles.filter((t) => t.owner === 'red').length / 60 },
    order: units.filter((u) => u.kind !== 'building').map((u) => u.id),
    turn: 3,
  };
}

const DEMO_SURGE = { label: '妖力', stage: 1, stageName: '壱', maxStage: 3, points: 0, nextAt: 2, toNext: 2, progress: 0, thresholds: [0, 2, 5], key: null, traits: [], nextTraits: [] };

const ENEMY_BOSS = (ch: number) => `e${String(ch * 10).padStart(3, '0')}`;
